/**
 * Google Analytics cookies, read on the storefront and carried along with the handoff.
 *
 * Runs on the merchant's own domain, inside the customer's browser. Nothing here may import
 * from `@/` — the bundle is served from our origin but executes as first-party code on the
 * shop, so it must be self-contained (see `docs/STOREFRONT.md`).
 *
 * ## Why here, and not on the checkout page
 *
 * GA4's tag stores its ids in **first-party cookies of the shop's domain**: `_ga` carries the
 * client id and `_ga_<SUFFIX>` (one per GA4 property) carries the session id. The hosted
 * checkout runs on another origin and can never read them, so the only place where a purchase
 * can be tied back to the session that started with an ad click is this script, at the moment
 * the customer leaves the shop. The values travel inside the create payload, are stored on the
 * `Checkout` row and are handed to GA4's Measurement Protocol by the exporter — the same
 * `client_id` and `session_id` the tag would have sent, which is what makes the conversion
 * land in the original session instead of a new "(direct)" one.
 *
 * ## Consent, by construction — and what it does and does not cover
 *
 * This module reads **only** `_ga` and `_ga_*`, and only if they exist. When the shopper
 * declined analytics cookies, Google's tag (behind the shop's consent banner or Consent Mode)
 * never set them, so **no cookie data** is read or sent: capture-if-present means the script
 * never has to know which consent tool the theme uses, and *attribution* — joining the
 * purchase to the session the ad click started — follows the banner. It is not a consent gate
 * on *measurement*: the GA4 exporter still reports the purchase, under a pseudonymous id
 * derived from the checkout (`src/lib/funnel/exporters/ga4.ts`), exactly as the Meta, TikTok
 * and Pinterest exporters do — server-side measurement follows the tenant's lawful basis on
 * every ad platform (`DECISIONS.md` D83, `docs/EXPORTERS.md` §3.4). Whether GA4 should instead
 * be gated on the cookies being present is the owner's call, not this module's. No other
 * cookie is looked at by this module — the Google Ads click-id cookies (`_gcl_*`) have their
 * own reader, `./google-click-cookies.ts`, under the same capture-if-present rule, and
 * {@link readAnalyticsCookies} joins the two into the one `analytics` block, filling any click
 * id Google's cookie lacks from Peak's own `_peak_gcl` (`./url-click-ids.ts`) — and a cookie jar
 * that is unavailable (a sandboxed frame, a browser that throws on `document.cookie`) yields an
 * empty result rather than an error — analytics must never stand between the customer and the
 * checkout.
 *
 * ## The cookie layout
 *
 * Google publishes no document for the cookie formats; what follows is the observed behaviour
 * of the tag, pinned by `tests/unit/storefront/ga-cookies.test.ts`:
 *
 * - `_ga` = `GA1.1.1234567890.1700000000`. The first two fields are a version and the
 *   domain-depth (`GA1.2.` on older tags); the **client id is everything after the second
 *   dot**, `1234567890.1700000000`.
 * - `_ga_<SUFFIX>` = `GS1.1.1700000000.1.1.1700000100.0.0.0` on the older layout, where the
 *   session id is the **third** dot-field, or
 *   `GS2.1.s1700000000$o1$g0$t1700000100$j0$l0$h0` on the newer one, where the third
 *   dot-field is a `$`-separated list and the session id is its `s<digits>` token. `SUFFIX`
 *   is the measurement id without its `G-` prefix.
 *
 * Anything that does not match is dropped, per cookie: a client id that is not two numbers,
 * a session field that is not digits fitting an `int64`, a suffix that is not a measurement
 * id. Everything is bounded — a cookie value longer than {@link PEAK_GA_MAX_COOKIE_LENGTH} is ignored and at
 * most {@link PEAK_GA_MAX_SESSIONS} properties are kept — so a hostile or broken cookie jar
 * cannot grow the payload.
 *
 * The three shapes are exported ({@link GA_CLIENT_ID_PATTERN}, {@link GA_SESSION_ID_PATTERN},
 * {@link GA_CONTAINER_SUFFIX_PATTERN}) and are the **single source**: the create endpoint
 * validates the block against them before storing it (`src/lib/checkout/create/create-checkout.ts`)
 * and the GA4 exporter checks the stored columns against them before sending
 * (`src/lib/funnel/exporters/ga4.ts`). This file has no `@/` imports, so server code may
 * import from it; the bundle rule (`docs/STOREFRONT.md`) constrains what the *bundle* imports,
 * not who imports the bundle's sources.
 */

import { readGoogleClickCookies, type YcAdClickIds } from './google-click-cookies';
import { readPeakGclCookie } from './url-click-ids';

/**
 * What the storefront adds to the create payload as `analytics`. Every member is absent when
 * unknown. The Google Ads click ids come from the sibling reader (`./google-click-cookies.ts`)
 * and ride in the same block, so the wire format is one object:
 * `{ gaClientId?, gaSessions?, adClickIds?: { gclid?, wbraid?, gbraid?, dclid? } }`.
 */
export type YcGaAnalytics = {
  /** The GA4 client id: `<random>.<first-visit timestamp>`, two numbers. */
  gaClientId?: string;
  /** Session id per GA4 property, keyed by the measurement id without `G-`. */
  gaSessions?: Record<string, string>;
  /** The Google Ads click ids from the `_gcl_*` cookies (`./google-click-cookies.ts`). */
  adClickIds?: YcAdClickIds;
};

/** A cookie value longer than this is not one Google wrote; it is ignored. */
export const PEAK_GA_MAX_COOKIE_LENGTH = 128;

/** How many `_ga_<SUFFIX>` cookies are kept. A shop rarely runs more than two properties. */
export const PEAK_GA_MAX_SESSIONS = 8;

/**
 * A GA4 client id once the `GA1.<depth>.` prefix is gone: two dot-separated integers,
 * `<random>.<first-visit timestamp>`. Up to 20 digits each — a cookie Google wrote has ten —
 * so the value can be kept as the string it is and never goes through `Number()`.
 */
export const GA_CLIENT_ID_PATTERN = /^\d{1,20}\.\d{1,20}$/;

/**
 * A GA4 session id as the `_ga_<SUFFIX>` cookie carries it: digits, in practice Unix seconds.
 * Kept and sent as a **digit string**: the Measurement Protocol types `session_id` as an
 * `int64` and accepts a string that parses as one (probed 2026-09-21 against the debug
 * endpoint: `"12345678901234567"` — 17 digits, past 2^53 — is accepted exactly, where
 * `Number()` would have altered it; `"9223372036854775808"`, one over `int64`, is
 * `VALUE_INVALID`). So the shape is at most 19 digits, and {@link isGaSessionId} adds the
 * `int64` ceiling on a 19-digit value. Google's own cookies carry ten digits.
 */
export const GA_SESSION_ID_PATTERN = /^\d{1,19}$/;

/** `int64` max, as a string of the same length a 19-digit id has, so `<=` compares digits. */
const GA_SESSION_ID_MAX = '9223372036854775807';

/**
 * Whether a value is a session id GA4 will take: digits that fit an `int64`. The one rule the
 * storefront parser, the create endpoint and the exporter all apply.
 */
export function isGaSessionId(value: string): boolean {
  if (!GA_SESSION_ID_PATTERN.test(value)) return false;
  return value.length < GA_SESSION_ID_MAX.length || value <= GA_SESSION_ID_MAX;
}

/** A measurement id without its `G-` prefix, which is how `_ga_<SUFFIX>` cookies are named. */
export const GA_CONTAINER_SUFFIX_PATTERN = /^[A-Z0-9]{4,24}$/;

const CLIENT_ID = GA_CLIENT_ID_PATTERN;
const CLIENT_ID_PREFIX = /^GA1\.\d+\./;
const SESSION_TOKEN = /^s(\d{1,19})(\$|$)/;
const SESSION_VERSION = /^GS\d+$/;
const CONTAINER_SUFFIX = GA_CONTAINER_SUFFIX_PATTERN;
const CLIENT_COOKIE = '_ga';
const SESSION_COOKIE_PREFIX = '_ga_';

/**
 * The client id inside a `_ga` cookie value, or `null`.
 *
 * The `GA1.<depth>.` prefix is stripped when present; what remains must be exactly two
 * numbers. A value that is already a bare client id is accepted as well, so a tag that ever
 * drops the prefix keeps working.
 */
export function parseGaClientId(value: string): string | null {
  if (value.length > PEAK_GA_MAX_COOKIE_LENGTH) return null;
  const id = value.replace(CLIENT_ID_PREFIX, '');
  return CLIENT_ID.test(id) ? id : null;
}

/**
 * The session id inside a `_ga_<SUFFIX>` cookie value, or `null`.
 *
 * `GS1.1.<session>.…` and `GS2.1.s<session>$…` both keep the session in the third dot-field;
 * the two layouts differ only in how that field is written. The version field must look like
 * one Google writes, so an unrelated cookie that happens to start with `_ga_` is dropped.
 */
export function parseGaSessionId(value: string): string | null {
  if (value.length > PEAK_GA_MAX_COOKIE_LENGTH) return null;
  const fields = value.split('.');
  if (fields.length < 3 || !SESSION_VERSION.test(fields[0] as string)) return null;

  const field = fields[2] as string;
  if (isGaSessionId(field)) return field;

  const token = SESSION_TOKEN.exec(field);
  if (token === null) return null;
  const session = token[1] as string;
  return isGaSessionId(session) ? session : null;
}

/**
 * Parse a `document.cookie` string.
 *
 * Pure: takes the string, returns whatever Google Analytics ids it holds. Cookie names are
 * matched exactly, values are **not** decoded (Google writes plain ASCII, and a `%` in one
 * of these cookies means it is not one of Google's), and the first *valid* occurrence of a
 * name wins when a cookie is duplicated across paths or domains — the browser lists the most
 * specific one first, and an entry that does not parse neither wins nor blocks a later one.
 */
export function parseGaCookies(cookieString: string): YcGaAnalytics {
  const result: YcGaAnalytics = {};
  if (typeof cookieString !== 'string' || cookieString === '') return result;

  const sessions: Record<string, string> = {};
  let sessionCount = 0;

  const parts = cookieString.split(';');
  for (let i = 0; i < parts.length; i += 1) {
    const part = (parts[i] as string).trim();
    const eq = part.indexOf('=');
    if (eq <= 0) continue;
    const name = part.slice(0, eq).trim();
    const value = part.slice(eq + 1).trim();

    if (name === CLIENT_COOKIE) {
      if (result.gaClientId !== undefined) continue;
      const clientId = parseGaClientId(value);
      if (clientId !== null) result.gaClientId = clientId;
      continue;
    }

    if (name.indexOf(SESSION_COOKIE_PREFIX) !== 0) continue;
    const suffix = name.slice(SESSION_COOKIE_PREFIX.length);
    if (!CONTAINER_SUFFIX.test(suffix)) continue;
    if (Object.prototype.hasOwnProperty.call(sessions, suffix)) continue;
    if (sessionCount >= PEAK_GA_MAX_SESSIONS) continue;

    const sessionId = parseGaSessionId(value);
    if (sessionId === null) continue;
    sessions[suffix] = sessionId;
    sessionCount += 1;
  }

  if (sessionCount > 0) result.gaSessions = sessions;
  return result;
}

/** The slice of `document` this module reads. Structural so it can be tested without a DOM. */
export type YcCookieDocumentLike = { cookie?: unknown };

/**
 * Read the Google Analytics ids off a document, never throwing.
 *
 * `document.cookie` can throw (a sandboxed iframe without `allow-same-origin`, a document
 * with an opaque origin, a locked-down WebView) or be something other than a string (a theme
 * that shadowed it). Either is "no cookies", and an empty result — the handoff goes ahead
 * without attribution rather than not at all.
 */
export function readGaCookies(
  documentLike: YcCookieDocumentLike | null | undefined,
): YcGaAnalytics {
  try {
    if (documentLike === null || documentLike === undefined) return {};
    const cookie = documentLike.cookie;
    return typeof cookie === 'string' ? parseGaCookies(cookie) : {};
  } catch {
    return {};
  }
}

/**
 * Read everything the `analytics` block carries: the GA4 ids and the Google Ads click ids,
 * each from its own reader, none ever throwing. The click ids are Google's `_gcl_*` cookies
 * first and Peak's own `_peak_gcl` (`./url-click-ids.ts`, D166) for every id Google's cookie
 * did not supply — Google wins per id. `adClickIds` is left out when neither has anything, so
 * the block never carries an empty member.
 */
export function readAnalyticsCookies(
  documentLike: YcCookieDocumentLike | null | undefined,
): YcGaAnalytics {
  const analytics = readGaCookies(documentLike);
  const google = readGoogleClickCookies(documentLike);
  const peak = readPeakGclCookie(documentLike);
  const adClickIds =
    google !== undefined && peak !== undefined ? Object.assign({}, peak, google) : google || peak;
  if (adClickIds !== undefined) analytics.adClickIds = adClickIds;
  return analytics;
}

/** Whether there is anything worth sending. */
export function hasGaAnalytics(analytics: YcGaAnalytics): boolean {
  return (
    analytics.gaClientId !== undefined ||
    analytics.gaSessions !== undefined ||
    analytics.adClickIds !== undefined
  );
}
